import { useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation as useRouterLocation } from 'react-router-dom'

import { useLocation } from '../../Location/Location.hook'
import { QuizQuestionContext, SCREEN_QUIZ_QUESTION } from '../../../contextAPI'
import { combineToFullName, PATH_URL, splitFullName, upsertQuizResponse, validate } from 'src/helpers'
import { isObjectEmpty } from 'src/helpers'
import { QuizType } from 'src/pages/shared'
import { isDateFirstEntry, pushQuizStep } from 'src/helpers/quizEntry'
import { IOption, QuizResponse } from 'src/types'

export const quizTakerRolesNew = [
  { label: "I'm getting married", value: 'BRIDE_GROOM' },
  { label: "I'm a wedding planner", value: 'PLANNER' },
  { label: "I'm helping with a wedding", value: 'FRIEND_FAMILY' },
  { label: 'Just browsing', value: 'BROWSING' },
]

interface formError {
  firstName?: boolean
  lastName?: boolean
  fullName?: boolean
  partnerName?: boolean
  partnerFirstName?: boolean
  partnerLastName?: boolean
}

export const useYourDetails = (quizType?: QuizType) => {
  const history = useHistory()
  const { search } = useRouterLocation()
  const { data, setQuizScreen, weddingDateData, setWeddingDateDataState } = useContext(QuizQuestionContext)
  const location = useLocation()
  const [formError, setFormError] = useState<formError>({})
  const [role, setRole] = useState<string>(data.planningStage?.role ?? 'BRIDE_GROOM')

  const isFullNameError = useMemo(() => !validate.leastOneLetter(weddingDateData.fullName || ''), [
    weddingDateData.fullName,
  ])
  const isPartnerNameError = useMemo(() => !validate.leastOneLetter(weddingDateData.partnerName || ''), [
    weddingDateData.partnerName,
  ])

  const formInvalid = () => {
    setFormError({
      fullName: isFullNameError,
      partnerName: isPartnerNameError,
    })
    return isFullNameError || isPartnerNameError
  }

  const handleNext = async () => {
    if (formInvalid()) {
      return
    }
    const currentTempPoppyData = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    const currentCustomerInfoLocal = currentTempPoppyData?.customerInfo || {}
    let responses: QuizResponse[] = currentTempPoppyData.quizResponses || []
    responses = upsertQuizResponse(responses, 'QUIZ_TAKER_ROLE', role)
    responses = responses.filter((e) => e.key !== 'PLANNER_STATUS')
    const nextTempPoppyData = { ...currentTempPoppyData, quizResponses: responses }
    if (!isObjectEmpty(currentTempPoppyData)) {
      nextTempPoppyData.customerInfo = {
        ...currentCustomerInfoLocal,
        firstName: weddingDateData.firstName?.trim() || '',
        lastName: weddingDateData.lastName?.trim() || '',
        partnerName: weddingDateData.partnerName?.trim() || '',
      }
    }
    localStorage.setItem('tempPoppySessionData', JSON.stringify(nextTempPoppyData))
    if (quizType === QuizType.PINTEREST) {
      history.push({
        pathname: PATH_URL.NEW_BUDGET_PAGE,
        search: new URLSearchParams({ quizType }).toString(),
      })
      setQuizScreen(SCREEN_QUIZ_QUESTION.NEW_BUDGET_PAGE)
    } else {
      pushQuizStep(history, PATH_URL.PLANNING_STAGE, search)
      setQuizScreen(SCREEN_QUIZ_QUESTION.PLANNING_STAGE)
    }
  }

  const handleBack = async () => {
    if (window.history.length > 1) {
      history.goBack()
    } else {
      if (quizType === QuizType.PINTEREST) {
        history.push({
          pathname: PATH_URL.PINTEREST_AVAILABILITY,
          search: new URLSearchParams({ quizType }).toString(),
        })
        setQuizScreen(SCREEN_QUIZ_QUESTION.PINTEREST_AVAILABILITY)
      } else if (isDateFirstEntry(search)) {
        pushQuizStep(history, PATH_URL.NEW_COLOR_PALETTE_PAGE, search)
        setQuizScreen(SCREEN_QUIZ_QUESTION.NEW_COLOR_PALETTE_PAGE)
      } else {
        history.push({ pathname: PATH_URL.NEW_AVAILABILITY_PAGE })
        setQuizScreen(SCREEN_QUIZ_QUESTION.NEW_AVAILABILITY_PAGE)
      }
    }
  }

  const handleChangeRole = (option: IOption | null) => {
    if (option) setRole(option.value)
  }

  const handleChangeInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target
    if (name === 'fullName') {
      const [firstName, lastName] = splitFullName(value)
      setWeddingDateDataState({ ...weddingDateData, [name]: value, firstName, lastName })
      setFormError((data) => ({ ...data, [name]: false, firstName: false, lastName: false }))
      return
    }
    if (name === 'partnerName') {
      const [partnerFirstName, partnerLastName] = splitFullName(value)
      setWeddingDateDataState({ ...weddingDateData, [name]: value, partnerFirstName, partnerLastName })
      setFormError((data) => ({ ...data, [name]: false, partnerFirstName: false, partnerLastName: false }))
      return
    }
    setWeddingDateDataState({ ...weddingDateData, [name]: value })
    setFormError((data) => ({ ...data, [name]: false }))
  }

  const nextDisabled = useMemo(() => {
    const check = !weddingDateData.fullName || !weddingDateData.partnerName || role === ''

    return check
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weddingDateData, role])

  useEffect(() => {
    setFormError((data) => ({ ...data, location: false }))
  }, [location.locationSelected])

  useEffect(() => {
    const dataFromLocal = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    const quizResponsesFromLocal: QuizResponse[] = dataFromLocal?.quizResponses || []
    const roleFromLocal = quizResponsesFromLocal.find((e) => e.key === 'QUIZ_TAKER_ROLE')
    setRole(data.planningStage?.role || (roleFromLocal?.values as string) || 'BRIDE_GROOM')
  }, [data.planningStage?.role])

  useEffect(() => {
    const currentTempDataFromLocalStorage = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    if (data.weddingDate) {
      const [partnerFirstName, partnerLastName] = splitFullName(
        data.weddingDate?.partnerName?.trim() || currentTempDataFromLocalStorage.customerInfo?.partnerName?.trim() || ''
      )
      setWeddingDateDataState({
        weddingDate:
          data?.weddingDate?.weddingDate ||
          currentTempDataFromLocalStorage.quizResponses?.find((e: QuizResponse) => e.key === 'EVENT_DATE') ||
          '',
        emailAddress: data?.weddingDate?.emailAddress,
        phoneNumber: data?.weddingDate?.phoneNumber,

        firstName: data?.weddingDate?.firstName || currentTempDataFromLocalStorage.customerInfo?.firstName || '',
        lastName: data?.weddingDate?.lastName || currentTempDataFromLocalStorage.customerInfo?.lastName || '',
        fullName:
          data?.weddingDate?.fullName?.trim() ||
          combineToFullName(
            currentTempDataFromLocalStorage.customerInfo?.firstName || '',
            currentTempDataFromLocalStorage.customerInfo?.lastName || ''
          ) ||
          '',

        partnerFirstName: partnerFirstName || '',
        partnerLastName: partnerLastName || '',
        partnerName: data?.weddingDate?.partnerName || currentTempDataFromLocalStorage.customerInfo?.partnerName || '',
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, setWeddingDateDataState])

  useEffect(() => {
    const tempData = JSON.parse(localStorage.getItem('tempPoppySessionData') || '{}')
    if (tempData.customerInfo) {
      setWeddingDateDataState({
        ...weddingDateData,
        firstName: tempData.customerInfo.firstName,
        lastName: tempData.customerInfo.lastName,
        fullName: combineToFullName(tempData.customerInfo.firstName, tempData.customerInfo.lastName),
        partnerFirstName: tempData.customerInfo.partnerFirstName,
        partnerLastName: tempData.customerInfo.partnerLastName,
        partnerName: tempData.customerInfo.partnerName,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    data,
    location,
    nextDisabled,
    weddingDateData,
    formError,
    role,
    handleNext,
    handleBack,
    handleChangeInput,
    handleChangeRole,
  }
}
